import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { IntlProvider } from 'react-intl-next';

import { maxHeight } from '@/styles';
import {
  useEntityController,
  EditContext,
  CreateEntityContext,
  DeleteEntityContext,
  useCreateEntityController,
  useDeleteEntityController,
  useSearchConfigController,
  useFeatureGatesController,
  FeatureGatesContext,
} from '@/controllers';
import { AuthProvider, OrgUserProvider, GlobalProvider } from '@/components/providers';

import { FetchError } from './services/api-service/api-service';
import { SearchConfigContext } from './controllers/search-config-controller';
import useStageDisplayController, {
  StageDisplayContext,
} from './controllers/stage-display-controller';
import { FCC } from './utils/types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count: number, err: FetchError) => {
        const status = err.response?.status;
        return count < 3 ? !!status && (status < 400 || status >= 500) : false;
      },
      refetchOnWindowFocus: false,
    },
  },
});

const Contexts: FCC = props => {
  const editController = useEntityController();
  const createController = useCreateEntityController();
  const deleteController = useDeleteEntityController();
  const searchConfigController = useSearchConfigController();
  const stageDisplayController = useStageDisplayController();
  const featureGates = useFeatureGatesController();

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <OrgUserProvider>
          <FeatureGatesContext.Provider value={featureGates}>
            <IntlProvider locale="en">
              <BrowserRouter>
                <QueryParamProvider adapter={ReactRouter6Adapter}>
                  <GlobalProvider>
                    <CreateEntityContext.Provider value={createController}>
                      <DeleteEntityContext.Provider value={deleteController}>
                        <EditContext.Provider value={editController}>
                          <SearchConfigContext.Provider value={searchConfigController}>
                            <StageDisplayContext.Provider value={stageDisplayController}>
                              <div id="body" css={[maxHeight]} {...props} />
                            </StageDisplayContext.Provider>
                          </SearchConfigContext.Provider>
                        </EditContext.Provider>
                      </DeleteEntityContext.Provider>
                    </CreateEntityContext.Provider>
                  </GlobalProvider>
                </QueryParamProvider>
              </BrowserRouter>
            </IntlProvider>
          </FeatureGatesContext.Provider>
        </OrgUserProvider>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default Contexts;
